import { UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Modal, Select, Spinner } from "@/components/common";
import { useAgentsForSelect } from "@/hooks/useAgents";
import { useAssignAgent, useClientPerson } from "@/hooks/useClients";
import {
  ConsentBadges,
  formatDateTime,
  RodoVerifiedMark,
} from "./RodoStatusBadges";

interface ClientPersonModalProps {
  clientId: string | null;
  onClose: () => void;
}

/**
 * Administrator view of one person (matched by PESEL) across agents. Every agent has a
 * separate record and a separate consent, because the clause names the collecting agent.
 */
export function ClientPersonModal({
  clientId,
  onClose,
}: ClientPersonModalProps) {
  const { data, isLoading, isError, error } = useClientPerson(clientId);
  const first = data?.records[0];
  const { data: agents = [] } = useAgentsForSelect();
  const assign = useAssignAgent();
  const [agentId, setAgentId] = useState("");

  const assigned = new Set(data?.records.map((r) => r.agentId));
  const agentOptions = agents
    .filter((a) => !assigned.has(a.id))
    .map((a) => ({ value: a.id, label: a.fullName }));

  function handleAssign() {
    if (!clientId || !agentId) {
      return;
    }
    assign.mutate(
      { clientId, agentId },
      {
        onSuccess: (record) => {
          setAgentId("");
          toast.success(
            `Dodano agenta ${record.agentName}. Klient dostał SMS${record.emailMasked ? " i e-mail" : ""} z jego danymi.`,
          );
        },
      },
    );
  }

  return (
    <Modal
      isOpen={clientId !== null}
      onClose={onClose}
      title={
        first
          ? `${first.firstName} ${first.lastName} — u wszystkich agentów`
          : "Klient u wszystkich agentów"
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : isError || !data ? (
        <Alert variant="error">
          {(error as { message?: string } | null)?.message ??
            "Nie udało się wczytać danych"}
        </Alert>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-sub">
            PESEL {data.peselMasked} · {data.records.length}{" "}
            {data.records.length === 1 ? "agent" : "agentów"}. Każdy agent
            zbiera własną zgodę — klauzula wskazuje go imiennie.
          </p>
          <ul className="space-y-3">
            {data.records.map((r) => (
              <li
                key={r.clientId}
                className="space-y-2 rounded-lg border border-edge-soft p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-main">
                    {r.agentName}
                    {r.agentRauNumber && (
                      <span className="ml-1 font-normal text-faint">
                        · RAU {r.agentRauNumber}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-faint">
                    dodany {formatDateTime(r.createdAt)} · wniosków:{" "}
                    {r.requestCount}
                  </p>
                </div>
                <p className="text-xs text-sub">
                  {r.phoneMasked}
                  {r.emailMasked && ` · ${r.emailMasked}`}
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <RodoVerifiedMark rodo={r.rodo} />
                  <ConsentBadges rodo={r.rodo} />
                </div>
              </li>
            ))}
          </ul>

          <div className="space-y-3 border-t border-edge-soft pt-4">
            <p className="text-sm font-medium text-main">
              Dodaj agenta obsługującego
            </p>
            <p className="text-xs text-faint">
              Nowy agent dostanie własny rekord tego klienta (bez zgody — musi
              wysłać własny wniosek RODO). Klient otrzyma SMS i e-mail z danymi
              kontaktowymi nowego agenta.
            </p>
            {assign.isError && (
              <Alert variant="error">
                {(assign.error as { message?: string }).message ??
                  "Nie udało się dodać agenta"}
              </Alert>
            )}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Select
                options={agentOptions}
                value={agentId}
                onChange={setAgentId}
                placeholder={
                  agentOptions.length
                    ? "Wybierz agenta..."
                    : "Brak innych agentów"
                }
                className="flex-1"
              />
              <Button
                onClick={handleAssign}
                isLoading={assign.isPending}
                disabled={!agentId}
                leftIcon={<UserPlus size={16} aria-hidden />}
              >
                Dodaj i powiadom klienta
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
